import React, { Dispatch, SetStateAction, useEffect } from 'react';
import {
  Controller,
  FieldValues,
  FormProvider,
  UseFormReturn,
} from 'react-hook-form';
import { MultiStepFormStep } from '@monofront/business/stateMachines';
import {
  ACTIONS,
  AddressFormEvent,
  MultiStepFormStepType,
} from '@monofront/business/stateMachines/multiStepForm/types';
import AddressForm from '@slices/AddressForm';
import { AddressFormSlice, MultiSelectCollectionSlice } from '@monofront/types';
import {
  BaseActionObject,
  EventData,
  ResolveTypegenMeta,
  SCXML,
  ServiceMap,
  SingleOrArray,
  State,
  TypegenDisabled,
} from '@monofront/xstate-v4';
import {
  Context,
  Events,
} from '@monofront/business/stateMachines/multiStepForm/types';
import { logger } from '@monofront/logging';
import MultiSelectCollection from '@slices/MultiSelectCollection';

export interface IMultiStepFormProps {
  steps: Record<string, MultiStepFormStep>;
  form: UseFormReturn<FieldValues>;
  state: State<
    Context,
    Events,
    any,
    {
      value: any;
      context: Context;
    },
    ResolveTypegenMeta<TypegenDisabled, Events, BaseActionObject, ServiceMap>
  >;
  send: (
    event: SCXML.Event<Events> | SingleOrArray<any>,
    payload?: EventData,
  ) => State<Context, Events, any, any>;
  isValidating: boolean;
  setIsValidating: Dispatch<SetStateAction<boolean>>;
  currentStep: MultiStepFormStep;
}

const MultiStepForm: React.FC<IMultiStepFormProps> = ({
  state,
  send,
  form,
  setIsValidating,
  currentStep,
}) => {
  useEffect(() => {
    const validate = async () => {
      setIsValidating(true);
      await form.trigger();
      setIsValidating(false);
    };

    if (state.value !== 'page_loader') {
      form.setValue(currentStep.id, state.context.savedCollections);
    }

    void validate();
  }, [state.value]);

  const currentStepWatch = form.watch(currentStep.id);

  useEffect(() => {
    if (currentStepWatch?.label) {
      const { countryCode: _countryCode, ...rest } = currentStepWatch;

      send(ACTIONS.SELECT_ADDRESS, {
        payload: {
          ...rest,
          country: currentStepWatch.countryCode,
        } as AddressFormEvent,
      });
    }
  }, [currentStepWatch]);

  if (currentStep.uid === 'page_loader') {
    return null;
  }

  return (
    <FormProvider {...form}>
      <div>
        {(() => {
          switch (currentStep.type) {
            case MultiStepFormStepType.AddressForm:
              return (
                <Controller
                  name={currentStep.id}
                  control={form.control}
                  render={() => (
                    <AddressForm
                      slice={currentStep.body[0] as AddressFormSlice}
                      index={0}
                      slices={[]}
                      context={undefined}
                      onChange={(value) => {
                        send(ACTIONS.SELECT_ADDRESS, {
                          payload: { ...value, country: value.countryCode },
                        });
                      }}
                    />
                  )}
                />
              );
            case MultiStepFormStepType.MultiSelectCollection:
              return (
                <Controller
                  name={currentStep.id}
                  control={form.control}
                  render={({ field: { onChange, value } }) => (
                    <MultiSelectCollection
                      slice={currentStep.body[0] as MultiSelectCollectionSlice}
                      index={0}
                      slices={[]}
                      context={undefined}
                      onChange={onChange}
                      formValue={
                        value?.label ? state.context.savedCollections : value
                      }
                    />
                  )}
                />
              );
            default:
              logger.info('no slice found for first order form');

              return null;
          }
        })()}
      </div>
    </FormProvider>
  );
};

const MemoizedMultiStepForm = React.memo(MultiStepForm);

export default MemoizedMultiStepForm;
